import {
  ActivitySpinnerSize,
  ActivitySpinnerWidget,
  ButtonType,
  ButtonWidget,
  CheckBoxWithLabelGroupWidget,
  CheckBoxWithLabelProps,
  CheckBoxWithLabelWidget
} from '@tableau/widgets';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { defaultMemoize } from 'reselect';
import * as _ from 'underscore';
import { Formatters, Messages } from '../../../codegen/Localize';
import { MixedContentItemDefinition } from '../../../ts/models/ContentDefinitions';
import { ExplorableItemType } from '../../../ts/models/ContentType';
import { ExploreContentApi_ContentType } from '../../../ts/ServerApi/types/IContent';
import { SpaceInfoTypeContentCount } from '../../state/ContentCountsState';
import { ExplorerToolbarTestIds } from './ExplorerToolbar';
import { swapWorkbookAndViewContentTypes, toggleSelectedContentType } from './FilterByTypeDropdown';
import { FilterByTypeDropdownHelpers } from './FilterByTypePanelHelpers';
import { FilterByTypePanelStyles } from './FilterByTypePanelStyles';

const ShowWorkbooksAsViewsValue = 'showWorkbooksAsViews';
const sharedWorkbookOrViewIndex = 1;
/**
 * Note: How content type filter options currently work:
 * 1. In favorite space, we show both workbook and view.
 * 2. In explorer space, we allow user to have an option to choose how these two should be shown
 * 3. In Shared with me space, we don't show DataRole content type
 *
 * How we populate these options?
 * All the options by default are pulled from ContentDefinition (MixedContentInOrder).
 * The contentFilter array in StateProps hold selected value in dropdown, not options to show in dropdown
 *
 * If toggleShowWorkbooksAsViews function is defined,
 *    then we check showWorkbooksAsViews to select what to show for workbook and view
 * otherwise,
 *    we show both workbook and view (Favorite space)
 *
 * In case you want to hide a content type,
 *    then add that content type to hiddenContentTypes
 */
export interface FilterByTypePanelStateProps {
  readonly contentCounts: SpaceInfoTypeContentCount<ExploreContentApi_ContentType>;
  readonly contentFilter: ReadonlyArray<ExplorableItemType>;
  readonly showWorkbooksAsViews: boolean;
  readonly flowsEnabled: boolean;
  readonly hiddenContentTypes: ReadonlyArray<ExplorableItemType>;
}

export interface FilterByTypePanelDispatchProps {
  readonly onComponentDidUpdate: () => void;
}

/**
 * when creating FilterByTypePanel, if `toggleShowWorkbooksAsViews` defined, panel will have showWorkbooksAsViews option,
 * otherwise will omit option and show both workbooks and views in panel together (like in Favorites Space)
 */
export interface FilterByTypePanelOwnProps {
  readonly defaultContentTypeFilterValues: ReadonlyArray<ExplorableItemType>;
  readonly toggleShowWorkbooksAsViews?: (showWorkbooksAsViews: boolean) => void;
  readonly filterSelected: (selectedItems: ArrayLike<ExplorableItemType>) => void;
  readonly closePanel: () => void;
  readonly contentItemDefinitions: MixedContentItemDefinition[];
}

export type FilterByTypePanelProps = FilterByTypePanelOwnProps & FilterByTypePanelDispatchProps & FilterByTypePanelStateProps;

export interface FilterByTypePanelState {
  // true when all content types have been manually checked one by one
  readonly allContentTypesChecked: boolean;
}

export class FilterByTypePanel extends React.PureComponent<FilterByTypePanelProps, FilterByTypePanelState>  {
  public constructor(props: FilterByTypePanelProps) {
    super(props);
    this.state = {allContentTypesChecked: false};
  }

  public componentDidMount(): void {
    // We want to update on mount as well
    this.props.onComponentDidUpdate();
  }

  public componentDidUpdate(): void {
    this.props.onComponentDidUpdate();
  }

  public render(): JSX.Element {
    const contentTypeCheckboxes =
      <div className={FilterByTypePanelStyles.PanelItems}
           data-tb-test-id={ExplorerToolbarTestIds.FilterByTypeDropdownContentTypeGroup}>
        <CheckBoxWithLabelGroupWidget
          onKeyDown={this.handleKeyDown}
          aria-labelledby={ExplorerToolbarTestIds.FilterByTypeDropdownButtonLabel}
          checkBoxWithLabelPropsList={this.memoizedGetCheckboxProperties(
            this.props.contentFilter,
            !!this.props.toggleShowWorkbooksAsViews,
            this.props.showWorkbooksAsViews,
            this.props.flowsEnabled,
            this.props.contentCounts,
            this.state.allContentTypesChecked,
            this.props.hiddenContentTypes,
            this.props.contentItemDefinitions
          )}
        />
      </div>;

    const clearButton =
      <div className={FilterByTypePanelStyles.ClearButton}>
        <ButtonWidget
          buttonType={ButtonType.LowEmphasis}
          onClick={this.clear}
          testId={`${ExplorerToolbarTestIds.FilterByTypeDropdownItem}-clear`}
          disabled={this.contentTypesEmptyCheckedState() && !this.props.showWorkbooksAsViews}
        >
          {Messages.clear()}
        </ButtonWidget>
      </div>;

    const showWorkbooksAsViewsCheckbox =
    <div className={FilterByTypePanelStyles.PanelItems}>
      <CheckBoxWithLabelWidget
        checked={this.props.showWorkbooksAsViews}
        handleChange={this.memoizedHandleShowWorkbooksAsViews(this.props.showWorkbooksAsViews)}
        label={Messages.filterByTypeDropdown_showWorkbooksAsViews()}
        disabled={!FilterByTypeDropdownHelpers.showingViewsOrWorkbooks(this.props.contentFilter)}
        testId={`${ExplorerToolbarTestIds.FilterByTypeDropdownItem}-${ShowWorkbooksAsViewsValue}`}
        removeLeftMargin={true}
      />
    </div>;

    return (
      <div data-tb-test-id={`${ExplorerToolbarTestIds.FilterByTypeDropdown}-Panel`} className={FilterByTypePanelStyles.Panel}>
        <div className={FilterByTypePanelStyles.PanelContent}>
          {contentTypeCheckboxes}
          {this.props.toggleShowWorkbooksAsViews &&
            <>
              <div className={FilterByTypePanelStyles.OptionsSeparator}>
                {Messages.filterByTypeDropdown_options()}
              </div>
              {showWorkbooksAsViewsCheckbox}
            </>
          }
        {clearButton}
        </div>
      </div>);
  }

  private handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = e => {
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'Home' || e.key === 'End') {
      e.preventDefault();
      e.stopPropagation();
      const checkboxes = e.currentTarget.querySelectorAll<HTMLInputElement>('input[type="checkbox"]:enabled');
      const focusIndex: number = Array.prototype.indexOf.call(checkboxes, e.target);
      const nextIndexMap: { [key: string]: number } = {
        'Home': 0,
        'End': checkboxes.length - 1,
        'ArrowDown': Math.min(focusIndex + 1, checkboxes.length - 1),
        'ArrowUp': Math.max(focusIndex - 1, 0),
      };
      checkboxes[nextIndexMap[e.key]].focus();
    }

    if (e.shiftKey && e.key === 'Tab') {
      e.preventDefault();
      e.stopPropagation();
      this.props.closePanel();
    }
  }

  private filteringOnAllContentTypes(): boolean {
    return this.props.contentFilter.length === this.props.defaultContentTypeFilterValues.length;
  }

  /**
   * when filtering on all content types, content type checkboxes should be in an unchecked state,
   * unless each content type has been selected manually one at a time while the dropdown is open
   *
   * returns true if all content types are being filtered on AND their checkboxes have
   * not been manually checked (does not account for checked state of showWorkbooksAsViews)
   */
  private contentTypesEmptyCheckedState(): boolean {
    return this.filteringOnAllContentTypes() && !this.state.allContentTypesChecked;
  }

  private getCheckboxProperties = (
    contentFilter: ReadonlyArray<ExplorableItemType>,
    useShowWorkbooksAsViews: boolean,
    showWorkbooksAsViewsValue: boolean,
    flowsEnabled: boolean,
    contentCounts: SpaceInfoTypeContentCount<ExploreContentApi_ContentType>,
    allContentTypesChecked: boolean, // need to update if clearing when allContentTypes in checked state
    hiddenContentTypes: ReadonlyArray<ExplorableItemType>,
    contentItemDefinitions: MixedContentItemDefinition[]
  ): CheckBoxWithLabelProps[] => {
    const contentDefinitions = FilterByTypeDropdownHelpers.getContentDefinitions(useShowWorkbooksAsViews, showWorkbooksAsViewsValue, flowsEnabled, hiddenContentTypes, contentItemDefinitions);
    return contentDefinitions
      .map((contentDef: MixedContentItemDefinition, index: number) => {
        const props = {
          checked: this.shouldCheck(contentDef.itemType, contentFilter),
          handleChange: this.handleChangeWrapper(contentDef.itemType),
          label: <LabelWithCount
            contentDefinition={contentDef}
            count={contentCounts && contentCounts[contentDef.itemType]}
          /> as unknown as string, // hack: cast to allow for component since widget only accepts string as label
          testId: `${ExplorerToolbarTestIds.FilterByTypeDropdownItem}-${contentDef.itemType}`,
          removeLeftMargin: true,
          // want first element of checkbox group to be tab accessible to tab between components in dropdown
          tabIndex: index === 0 ? 0 : -1,
        } as CheckBoxWithLabelProps;
        return props;
      });
  }

  private memoizedGetCheckboxProperties = defaultMemoize(this.getCheckboxProperties);

  private shouldCheck(value: string, currentTypeFilters: ReadonlyArray<ExplorableItemType>) {
    if (this.contentTypesEmptyCheckedState()) {
      return false;
    } else {
      return FilterByTypeDropdownHelpers.isCurrentlyFilteredContentType(value, currentTypeFilters);
    }
  }

  /**
   * CheckBoxWithLabel's handleChange prop requires a function that takes a boolean parameter.
   * Wraps a custom handleSelection function which takes a string so that the widget will accept it
   */
  private handleChangeWrapper = (value: string) => {
    return (checked: boolean) => {
      this.handleSelection(value);
    };
  }

  /**
   * Pulling out to eliminate unnecessary rerenders for showWorkbooksAsViews checkbox.
   * Want to call only when showWorkbooksAsViews changes.
   */
  private handleShowWorkbooksAsViews = (showWorkbooksAsViews: boolean) => {
    return (showWorkbooksAsViews: boolean) => {
      this.handleSelection(ShowWorkbooksAsViewsValue);
    };
  }
  private memoizedHandleShowWorkbooksAsViews = defaultMemoize(this.handleShowWorkbooksAsViews);

  private handleSelection = (value: string) => {
    const selectedContentTypes: ExplorableItemType[] = [...this.props.contentFilter];

    if (value === ShowWorkbooksAsViewsValue) {
      if (!this.props.toggleShowWorkbooksAsViews) {
        return;
      }
      const showWorkbooksAsViews = !this.props.showWorkbooksAsViews;
      this.props.toggleShowWorkbooksAsViews(showWorkbooksAsViews);
      swapWorkbookAndViewContentTypes(selectedContentTypes, showWorkbooksAsViews);
    } else if (selectedContentTypes.length === 1 && selectedContentTypes[0] === 'dataconnection' && value === 'datasource') {
      // Special case when data connection is being shown but user goes to unselect data source
      // Remove data connection from selected content types.
      selectedContentTypes.splice(0, 1);
    } else {
      if (this.contentTypesEmptyCheckedState()) {
        selectedContentTypes.splice(0, selectedContentTypes.length);
      }

      toggleSelectedContentType(selectedContentTypes, value as ExploreContentApi_ContentType);
      if(selectedContentTypes.length === this.props.defaultContentTypeFilterValues.length) {
        this.setState({allContentTypesChecked: true});
      }
    }

    // if selected content type contains dataconnection swap it with datasource
    const indexOfDataconnection = selectedContentTypes.indexOf('dataconnection');
    if (indexOfDataconnection > -1) {
      selectedContentTypes[indexOfDataconnection] = 'datasource';
    }

    if (selectedContentTypes.length > 0) {
      this.props.filterSelected(selectedContentTypes);
    } else {
      this.selectAll();
    }
  }

  // select all content types (do not change showWorkbooksAsViews setting)
  private selectAll = () => {
    const currentTypes = [...this.props.defaultContentTypeFilterValues];
    if(this.props.showWorkbooksAsViews) {
      currentTypes[sharedWorkbookOrViewIndex] = 'view';
    }
    this.props.filterSelected(currentTypes);
    this.setState({allContentTypesChecked: false});
  }

  // select all default content types (restores view --> workbook)
  private clear = () => {
    this.props.filterSelected(this.props.defaultContentTypeFilterValues);
    this.setState({allContentTypesChecked: false});

    // reset focus to first tabbable element
    const tabbable = (ReactDOM.findDOMNode(this) as HTMLElement).querySelector('[tabindex="0"]') as HTMLElement;
    tabbable.focus();
  }
}

interface LabelWithCountProps {
  readonly contentDefinition: MixedContentItemDefinition;
  readonly count: number;
}

class LabelWithCount extends React.PureComponent<LabelWithCountProps> {
  render() {
    return <>
      <span className={FilterByTypePanelStyles.Item}>
        {this.props.contentDefinition.pluralName()}
      </span>
      {Count(this.props.contentDefinition, this.props.count)}
    </>;
  }
}

const Count = (contentDef: MixedContentItemDefinition, count: number): JSX.Element => {
  if(!_.isUndefined(count) && !_.isNull(count)) {
    return <span data-tb-test-id={`${ExplorerToolbarTestIds.FilterByTypeDropdownItem}-${contentDef.itemType}-count`} data-raw-count={count}>
      ({Formatters.formatCount(count)})
    </span>;
  }

  // spinner shown on first load of panel
  return <>
    <div style={{display: 'inline-block'}}>
      <ActivitySpinnerWidget
        testId={`${ExplorerToolbarTestIds.FilterByTypeDropdownItem}-${contentDef.itemType}`}
        spinnerSize={ActivitySpinnerSize.Small}
      />
    </div>
  </>;
};
